import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { APIProvider } from '@vis.gl/react-google-maps';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { de } from 'date-fns/locale';

import PlaceAutocomplete from '../common/PlaceAutocomplete';
import { VEHICLE_TYPE_OPTIONS } from '../../config/vehicleTypes';
import { createRide } from '../../redux/slices/ridesSlice';

/**
 * Super-kompaktes "Neue Fahrt"-Formular für das LiveMap-Fahrten-Panel (~420px).
 * Dient dem schnellen Disponieren von Telefonbestellungen.
 *
 * Props:
 *  - initialDraft: { customerName, customerPhone, pickupAddress, incomingCallSessionId }
 *  - onClose:      schließt das Formular (zurück zur Fahrtenliste)
 *  - onCreated:    Callback nach erfolgreichem Anlegen (erhält die erstellte Fahrt)
 */
const QuickRideForm = ({ initialDraft, onClose, onCreated }) => {
  const dispatch = useDispatch();
  const { drivers } = useSelector((state) => state.drivers);

  const [mode, setMode] = useState('sofort'); // 'sofort' | 'vorbestellung'
  const [scheduledTime, setScheduledTime] = useState(() => new Date(Date.now() + 60 * 60 * 1000));

  const [name, setName] = useState(initialDraft?.customerName || '');
  const [phone, setPhone] = useState(initialDraft?.customerPhone || '');
  const [pickupAddress, setPickupAddress] = useState(initialDraft?.pickupAddress || '');
  const [pickupLoc, setPickupLoc] = useState(null); // { latitude, longitude }
  const [destAddress, setDestAddress] = useState('');
  const [destLoc, setDestLoc] = useState(null);
  const [vehicleType, setVehicleType] = useState(1);
  const [driverId, setDriverId] = useState('');
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Verfügbare Fahrer (analog RideForm): Sofort → online mit Fahrzeug; Vorbestellung → alle.
  const availableDrivers = useMemo(() => {
    if (!drivers || drivers.length === 0) return [];
    if (mode === 'vorbestellung') return drivers;
    return drivers.filter((d) => d.isOnline && (d.vehicle || d.car));
  }, [drivers, mode]);

  const handleModeChange = (_e, next) => {
    if (!next) return;
    setMode(next);
    // Fahrerauswahl zurücksetzen, da sich die verfügbare Menge ändert.
    setDriverId('');
  };

  const handleSubmit = async () => {
    if (!pickupAddress.trim()) {
      setError('Bitte eine Abholadresse angeben.');
      return;
    }
    if (mode === 'vorbestellung' && !scheduledTime) {
      setError('Bitte einen Zeitpunkt für die Vorbestellung angeben.');
      return;
    }

    const orderType = mode === 'vorbestellung' ? 1 : 2;
    let status;
    if (driverId) {
      status = orderType === 1 ? 'pre_accepted' : 'dispatcher_pending';
    } else {
      status = orderType === 1 ? 'scheduled' : 'open';
    }

    const selectedDriver = driverId ? availableDrivers.find((d) => d.id === driverId) : null;

    const payload = {
      customerId: null,
      customerName: name.trim() || 'Laufkunde',
      customerPhone: phone.trim() || null,
      isPhoneCustomer: true,
      start: pickupAddress,
      startLatitude: pickupLoc?.latitude ?? 0,
      startLongitude: pickupLoc?.longitude ?? 0,
      destination: destAddress || '',
      destinationLatitude: destLoc?.latitude ?? 0,
      destinationLongitude: destLoc?.longitude ?? 0,
      vehicleType: Number(vehicleType),
      orderType,
      status,
      notes: notes.trim() || null,
      ...(orderType === 1 ? { scheduledTime } : {}),
      ...(selectedDriver
        ? {
            driverId: selectedDriver.id,
            driverName: selectedDriver.name || selectedDriver.user?.name || 'Unbekannt',
            driverProfilePic:
              selectedDriver.profilePic || selectedDriver.user?.profile_picture || '',
            driverRating: selectedDriver.rating || null,
            driverRatingCount: selectedDriver.ratingCount || null,
            assignedByDispatcher: true,
            ...(orderType === 1
              ? { acceptedAt: new Date().toISOString() }
              : { is_active: false }),
            // Fahrzeugdaten nur bei Sofortbestellungen (bei Vorbestellung setzt die App sie später).
            ...(orderType === 2 && selectedDriver.vehicle
              ? {
                  driverVehicleId: selectedDriver.vehicle.id || null,
                  driverModel: selectedDriver.vehicle.model || null,
                  driverLicensePlate: selectedDriver.vehicle.licensePlate || null,
                }
              : {}),
          }
        : {}),
      ...(initialDraft?.incomingCallSessionId
        ? {
            incomingCallSessionId: initialDraft.incomingCallSessionId,
            bookingSource: 'incoming_call',
          }
        : {}),
    };

    setSubmitting(true);
    setError(null);
    try {
      const result = await dispatch(createRide(payload));
      if (!result.error) {
        onCreated?.(result.payload);
        onClose?.();
      } else {
        setError('Fahrt konnte nicht erstellt werden.');
      }
    } catch (e) {
      setError('Fahrt konnte nicht erstellt werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
      <Box
        sx={{
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          overflowY: 'auto',
          flex: 1,
        }}
      >
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ py: 0 }}>
            {error}
          </Alert>
        )}

        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={handleModeChange}
          size="small"
          fullWidth
          color="primary"
        >
          <ToggleButton value="sofort">Sofort</ToggleButton>
          <ToggleButton value="vorbestellung">Vorbestellung</ToggleButton>
        </ToggleButtonGroup>

        {mode === 'vorbestellung' && (
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
            <DateTimePicker
              label="Datum und Uhrzeit"
              value={scheduledTime}
              onChange={(value) => setScheduledTime(value)}
              ampm={false}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        )}

        <TextField
          label="Kunde-Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Leer lassen für Laufkunde (Telefonkunde ohne Profil)"
          InputLabelProps={{ shrink: true }}
          size="small"
          fullWidth
        />
        <TextField
          label="Telefon"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          size="small"
          fullWidth
        />

        <PlaceAutocomplete
          label="Abholadresse *"
          value={pickupAddress}
          onChange={(e) => {
            setPickupAddress(e.target.value);
            setPickupLoc(null);
          }}
          onPlaceSelect={(addr, loc) => {
            setPickupAddress(addr);
            setPickupLoc(loc);
          }}
          required
          size="small"
          fullWidth
        />
        <PlaceAutocomplete
          label="Zieladresse"
          value={destAddress}
          onChange={(e) => {
            setDestAddress(e.target.value);
            setDestLoc(null);
          }}
          onPlaceSelect={(addr, loc) => {
            setDestAddress(addr);
            setDestLoc(loc);
          }}
          size="small"
          fullWidth
        />

        <TextField
          select
          label="Fahrzeugtyp"
          value={vehicleType}
          onChange={(e) => setVehicleType(e.target.value)}
          size="small"
          fullWidth
          sx={{ mt: 1 }}
        >
          {VEHICLE_TYPE_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Zuweisung"
          value={driverId}
          onChange={(e) => setDriverId(e.target.value)}
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) => {
              if (!selected) {
                return <span style={{ color: 'rgba(0, 0, 0, 0.4)' }}>Fahrer (optional)</span>;
              }
              const d = availableDrivers.find((x) => x.id === selected);
              return d ? d.name || d.user?.name || 'Unbekannt' : '';
            },
          }}
          size="small"
          fullWidth
          helperText={
            mode === 'sofort'
              ? 'Nur Online-Fahrer mit Fahrzeug'
              : 'Alle Fahrer wählbar'
          }
        >
          <MenuItem value="">
            <em>Keine Zuweisung</em>
          </MenuItem>
          {availableDrivers.map((d) => (
            <MenuItem key={d.id} value={d.id}>
              {d.name || d.user?.name || 'Unbekannt'}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Notiz"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          size="small"
          fullWidth
          multiline
          minRows={2}
        />

        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
          <Button variant="outlined" color="inherit" fullWidth onClick={() => onClose?.()} disabled={submitting}>
            Abbrechen
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={submitting || !pickupAddress.trim()}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Fahrt erstellen
          </Button>
        </Box>
      </Box>
    </APIProvider>
  );
};

export default QuickRideForm;
